import React, { useRef, useState, useEffect } from 'react';
import T from 'components/T';
import { i18n } from '@international/mastodon-i18n';
import { ModalFooter, ModalHeader } from './DocumentVerificationComponents';
import { OcrDocumentSection, LoadingMessage } from './AiDocReview';
import {
  allOcrCompleted,
  getDocumentOptions,
  updateStateArray,
  isFourDigitYearOrInterval,
  getYearDropdownOptions,
  formatAcceptedTypesList,
  isEmpty,
  getSortedFileIndices,
} from './utils/formatter';
import { DateObject, DocumentInfoModalProps } from './utils/interfaces';
import {
  getSelectYearOption,
  selectYear,
  multiDocuments,
  defaultItem,
} from './utils/constants';
import { DocumentStatusParams, DocumentStatus } from './utils/status';

import './DocumentInfoModal.scss';

const DocumentInfoModal: React.FC<DocumentInfoModalProps> = ({
  files,
  documentsRequired,
  onDocumentsRequiredChange,
  handleUploadedFilesUpdate,
  onCancel,
  onSubmit,
  onRemoveByUploadId,
  documentTypes,
  aiOcrEnabled,
  screeningId,
  company,
  fullName,
  candidateDocContextEnabled = false,
}) => {
  const exceptionYears: DateObject[] = [
    { value: '', name: '', uploadId: '', disabled: true },
    ...(documentsRequired.some((doc: DateObject) => doc.value === selectYear)
      ? []
      : getSelectYearOption()),
    ...documentsRequired,
  ];

  const [state, setState] = useState({
    years: exceptionYears,
    selectedYears: Array(files.length).fill(''),
    selectedDocumentTypes: Array(files.length).fill(''),
    hasMissingDocuments: false,
    hasIncompleteMode: Array(files.length).fill(''),
    hasOcrInProgress: Array(files.length).fill(''),
    // Per-row open-editor signal surfaced by each OcrDocumentSection — gates
    // Submit on the OCR path (no row may be mid-edit). Non-fixable statuses
    // are forced false at the source.
    inEditMode: Array(files.length).fill(false),
    // EVERI-4122: per-row signal that a failed_to_process (or watchdog-
    // timeout) terminal is sitting at rest with Retry offered and not yet
    // resolved — also gates Submit, same as inEditMode above.
    hasUnresolvedFailure: Array(files.length).fill(false),
    statuses: {} as Record<number, DocumentStatus>,
    candidateContext: Array(files.length).fill(''), // EVERI-4005: threaded onto `years` at Submit
  });
  // keeps a mutable reference for async callbacks
  const stateRef = useRef(state);
  stateRef.current = state;

  // Keep a ref of files to use in async callbacks
  const filesRef = useRef(files);
  filesRef.current = files;

  // Auto-close when the candidate trashes the last row so the modal doesn't
  // linger as an empty box. The ref-gate avoids firing on the initial mount
  // window — the modal only opens once ≥1 upload has been reported, so an
  // empty `files` after that point can only be a trash click. `autoClosedRef`
  // ensures `onCancel` fires exactly once even if the parent re-renders with a
  // new `onCancel` reference while `files` is empty.
  const everHadFilesRef = useRef(false);
  const autoClosedRef = useRef(false);
  useEffect(() => {
    if (files.length > 0) {
      everHadFilesRef.current = true;
      return;
    }
    if (everHadFilesRef.current && !autoClosedRef.current) {
      autoClosedRef.current = true;
      onCancel();
    }
  }, [files, onCancel]);

  const handleRemoveDocument = (uploadId: string, exceptionId: string) => {
    const fileIndex = files.findIndex(file => file.uploadId === uploadId);

    const selectedYear = state.selectedYears[fileIndex];
    const updatedYears = state.years.map((year: any) => {
      if (year.value === selectedYear && year.uploadId === uploadId) {
        return { ...year, ...defaultItem };
      }
      return year;
    });

    setState(prevState => {
      const {
        selectedDocumentTypes,
        selectedYears,
        hasOcrInProgress,
        inEditMode,
        hasUnresolvedFailure,
        candidateContext,
      } = prevState;
      return {
        ...prevState,
        years: updatedYears,
        selectedDocumentTypes: selectedDocumentTypes.filter(
          (_, i) => i !== fileIndex,
        ),
        hasOcrInProgress: hasOcrInProgress.filter((_, i) => i !== fileIndex),
        inEditMode: inEditMode.filter((_, i) => i !== fileIndex),
        // EVERI-4122: parallels inEditMode above — else a later file reads a sibling's flag after removal.
        hasUnresolvedFailure: hasUnresolvedFailure.filter(
          (_, i) => i !== fileIndex,
        ),
        selectedYears: selectedYears.filter((_, i) => i !== fileIndex),
        // EVERI-4005: parallels the arrays above — else a later file reads a sibling's note after removal.
        candidateContext: candidateContext.filter((_, i) => i !== fileIndex),
      };
    });
    onDocumentsRequiredChange(updatedYears.slice(1));
    onRemoveByUploadId(uploadId, exceptionId);
  };

  const handleYearChange = (selectedYearValue: DateObject) => {
    const currentState = stateRef.current;
    const currentIndex = filesRef.current.findIndex(
      (file: any) => file.uploadId === selectedYearValue.uploadId,
    );

    const matchIndex = currentState.years.findIndex(
      year =>
        year.value === selectedYearValue.value &&
        year.value !== selectYear &&
        !year.disabled,
    );

    const updatedYears = currentState.years.map((year, i) => {
      if (i === matchIndex) {
        const updatedYear = {
          ...year,
          disabled: true,
          type: currentState.selectedDocumentTypes[currentIndex]?.value,
          requiredForVerification:
            currentState.selectedDocumentTypes[currentIndex]
              ?.requiredForVerification !== false,
          uploadId: selectedYearValue.uploadId,
          index: i,
          // Latch the candidate-manual marker forward. Sources, in order: the
          // year dropdown (candidate picked the year), the row's existing marker,
          // or the type the candidate hand-picked earlier (carried on
          // selectedDocumentTypes) when its year is only now being filled — incl.
          // by OCR. The OCR auto-fill path passes no flag, so auto-derived years
          // with no prior manual type/year stay unflagged.
          candidateLabeled:
            selectedYearValue.candidateLabeled ||
            year.candidateLabeled ||
            currentState.selectedDocumentTypes[currentIndex]
              ?.candidateLabeled ||
            false,
        };
        return updatedYear;
      }

      if (
        selectedYearValue.uploadId &&
        year.uploadId === selectedYearValue.uploadId &&
        i !== matchIndex
      ) {
        return {
          ...year,
          ...defaultItem,
        };
      }
      return year;
    });

    setState(prevState => ({
      ...prevState,
      years: updatedYears,
      hasMissingDocuments: false,
    }));
    updateStateArray(
      setState,
      'selectedYears',
      selectedYearValue.value,
      currentIndex,
    );
    onDocumentsRequiredChange(updatedYears.slice(1));
  };

  const handleDocumentTypeChange = (selectedTypeItem: DateObject) => {
    const currentState = stateRef.current;
    const currentIndex = filesRef.current.findIndex(
      (file: any) => file.uploadId === selectedTypeItem.uploadId,
    );

    const exceptionId = currentState.years.find(
      item => item?.exceptionType === multiDocuments,
    )?.id;

    const baseItem = {
      type: selectedTypeItem.value,
      requiredForVerification: selectedTypeItem.requiredForVerification,
      uploadId: selectedTypeItem.uploadId,
      // Candidate-manual marker — set only when the type was chosen from the
      // dropdown (the onChange wrapper tags `candidateLabeled: true`). The OCR
      // auto-classify path (helpers.handleSuccessResult / the initial
      // `otherType` seed) routes through this same handler without the tag, so
      // auto-derived types remain unflagged.
      candidateLabeled: selectedTypeItem.candidateLabeled || false,
    };

    const updatedYears = currentState.years.map((year: any) => {
      const isTargetYear =
        year.uploadId === selectedTypeItem.uploadId &&
        year.requiredForVerification;
      const isSelectedYear =
        year.value === currentState.selectedYears[currentIndex] &&
        year.requiredForVerification &&
        year.value !== selectYear;

      if (isTargetYear || isSelectedYear) {
        if (selectedTypeItem.requiredForVerification) {
          return {
            ...year,
            ...baseItem,
            // Preserve a marker the candidate already set on this row (e.g. via
            // the year dropdown). An OCR auto-classify routes through this same
            // handler without the flag and must not clobber it — mirrors the OR
            // in handleYearChange.
            candidateLabeled:
              selectedTypeItem.candidateLabeled || year.candidateLabeled,
          };
        }
        return {
          ...year,
          ...defaultItem,
        };
      }

      return year;
    });

    if (!selectedTypeItem.requiredForVerification) {
      updateStateArray(setState, 'selectedYears', '', currentIndex);
      updatedYears.push({
        ...baseItem,
        id: exceptionId,
        exceptionType: multiDocuments,
      });
    }

    setState(prevState => ({
      ...prevState,
      years: updatedYears,
      hasMissingDocuments: false,
    }));
    updateStateArray(
      setState,
      'selectedDocumentTypes',
      {
        value: selectedTypeItem.value,
        requiredForVerification: selectedTypeItem.requiredForVerification,
        // Persist the marker so a type hand-picked before its year row exists is
        // carried onto the year by handleYearChange (incl. an OCR-filled year).
        candidateLabeled: selectedTypeItem.candidateLabeled || false,
      },
      currentIndex,
    );
    onDocumentsRequiredChange(updatedYears.slice(1));
  };

  const allFieldsFilled = state.selectedYears.every((year, index) => {
    const type = state.selectedDocumentTypes[index];

    if (type.requiredForVerification === false) return true;
    return isFourDigitYearOrInterval(year) && !isEmpty(type);
  });

  const isOcrComplete = allOcrCompleted(state.hasOcrInProgress);

  // Submit is disabled while any OCR-eligible row has an open editor
  // (dropdowns visible, not yet Saved). No-op on the non-OCR path.
  const hasOpenEdits =
    aiOcrEnabled && isOcrComplete && (state.inEditMode || []).some(Boolean);

  // EVERI-4122 (Kristian): a Retry offered at rest (failed_to_process, or a
  // watchdog timeout) means the backend's last answer might not be its final
  // one — Submit must stay blocked until the candidate resolves it (retries
  // successfully, or trashes the row), not silently succeed on a doc that
  // never actually scanned.
  const hasUnresolvedFailure =
    aiOcrEnabled && (state.hasUnresolvedFailure || []).some(Boolean);

  const ocrEligible = aiOcrEnabled;
  const acceptedTypesText = formatAcceptedTypesList(documentTypes);

  const handleSubmit = () => {
    if (!allOcrCompleted(state.hasOcrInProgress) && aiOcrEnabled) return;
    if (hasOpenEdits) return;
    if (hasUnresolvedFailure) return;
    if (allFieldsFilled) {
      onSubmit();
      // EVERI-4005: merge each file's context draft onto its matching `years` entry before uploadedFilesUpdate reads it.
      const yearsWithContext = candidateDocContextEnabled
        ? state.years.map((year: DateObject) => {
            const fileIndex = files.findIndex(
              file => file.uploadId === year.uploadId,
            );
            const candidateContext =
              fileIndex >= 0 ? state.candidateContext[fileIndex] : '';
            return candidateContext ? { ...year, candidateContext } : year;
          })
        : state.years;
      handleUploadedFilesUpdate(yearsWithContext, files);
    } else {
      setState(prevState => ({
        ...prevState,
        hasMissingDocuments: true,
      }));
    }
  };

  const getDropdownClass = (hasError: boolean) =>
    hasError ? 'dropdown-wrapper error' : 'dropdown-wrapper';

  const renderDropdowns = (doc: any, i: number) => {
    const docTypeError =
      (state.hasMissingDocuments || state.hasIncompleteMode[i]) &&
      isEmpty(state.selectedDocumentTypes[i]);

    const yearError =
      (state.hasMissingDocuments || state.hasIncompleteMode[i]) &&
      (isEmpty(state.selectedYears[i]) ||
        state.selectedYears[i] === selectYear);

    // Type dropdown lists only the configured document types — no synthetic
    // "Other" option (matches staging/prod). Candidates classify with a real
    // type or trash the row.
    const documentOptions = getDocumentOptions(documentTypes);

    // Year dropdown shows only the requested years; years already satisfied by
    // another upload come through greyed out (`disabled: true`) and labelled
    // "{year} (used)" on the OCR path, forcing the candidate to trash this row
    // rather than double-claim a year. No "Other" option here — that lives only
    // in the document type dropdown.
    const yearOptions = getYearDropdownOptions(state.years, {
      includeUsedSuffix: ocrEligible,
      currentYear: state.selectedYears[i],
    });

    const yearLabel = i18n.getStr(
      ocrEligible
        ? 'components.Verification.AiDocReview.selectYear'
        : 'labels.documentYear',
    );

    return (
      <div className='dropdown-section'>
        <div className={getDropdownClass(docTypeError)}>
          <select
            name={`documentType-${i}`}
            aria-label={i18n.getStr('labels.documentType')}
            className='cp-form-control'
            value={state.selectedDocumentTypes[i]?.value || ''}
            onChange={e => {
              const option = documentOptions.find(
                o => o.value === e.target.value,
              );
              if (!option) return;
              // Candidate picked the type by hand — flag the selection so the
              // payload marks it `manually_labeled` (gated on required types in
              // uploadedFilesUpdate). Preserves the prior e.selectedItem contract.
              handleDocumentTypeChange({
                ...option,
                uploadId: doc.uploadId,
                candidateLabeled: true,
              });
            }}
          >
            {documentOptions.map(option => (
              <option key={`type-option-${option.value}`} value={option.value}>
                {/* The empty first option is the placeholder — its `name` is
                    blank in the data, so surface the field label instead. */}
                {option.value === ''
                  ? i18n.getStr('labels.documentType')
                  : option.name}
              </option>
            ))}
          </select>
        </div>

        {state.selectedDocumentTypes[i]?.requiredForVerification !== false && (
          <div className={getDropdownClass(yearError)}>
            <select
              name={`documentYear-${i}`}
              aria-label={yearLabel}
              className='cp-form-control'
              value={state.selectedYears[i] || ''}
              onChange={e => {
                const option = yearOptions.find(
                  o => o.value === e.target.value,
                );
                if (!option) return;
                handleYearChange({
                  ...option,
                  uploadId: doc.uploadId,
                  candidateLabeled: true,
                });
              }}
            >
              {yearOptions
                // Drop the redundant "Select year" sentinel; the empty option
                // below is the single labeled placeholder.
                .filter(option => option.value !== selectYear)
                .map(option => {
                  const isPlaceholder = option.value === '';
                  return (
                    <option
                      key={`year-option-${option.value}`}
                      value={option.value}
                      disabled={!isPlaceholder && option.disabled}
                    >
                      {/* Empty option is the placeholder (blank name in data);
                          surface the year label. Used years keep "{year} (used)". */}
                      {isPlaceholder ? yearLabel : option.name}
                    </option>
                  );
                })}
            </select>
          </div>
        )}
      </div>
    );
  };

  // EVERI-4248: singular copy once candidateDocContextEnabled caps the candidate to one document.
  const headerLabel = aiOcrEnabled
    ? `components.Verification.AiDocReview.${
        isOcrComplete
          ? candidateDocContextEnabled
            ? 'reviewDocument'
            : 'reviewDocuments'
          : candidateDocContextEnabled
          ? 'scanInProgressSingle'
          : 'scanInProgress'
      }`
    : 'components.employment.uploadSuccessful';

  // Static "Review your uploaded document(s)." instruction once scanning is done.
  const showReviewInstructions = aiOcrEnabled && isOcrComplete;

  const sortedFileIndices = getSortedFileIndices(files, state.statuses);

  return (
    <div
      className={
        aiOcrEnabled ? 'modal-overlay modal-overlay--review' : 'modal-overlay'
      }
    >
      <div className='cp-modal-content'>
        <ModalHeader
          onCancel={onCancel}
          textContent={i18n.getStr(headerLabel)}
        />
        <div className='cp-modal-body'>
          {!isOcrComplete && aiOcrEnabled && (
            <LoadingMessage
              candidateDocContextEnabled={candidateDocContextEnabled}
            />
          )}
          {showReviewInstructions && (
            <p className='review-instructions'>
              {i18n.getStr(
                `components.Verification.AiDocReview.${
                  candidateDocContextEnabled
                    ? 'reviewInstructionsSingle'
                    : 'reviewInstructions'
                }`,
              )}
            </p>
          )}
          {sortedFileIndices.map((i: number, idx: number) => {
            const status = state.statuses[i];
            const sortOrder = DocumentStatusParams[status]?.sortOrder;
            const prevStatus =
              idx > 0 ? state.statuses[sortedFileIndices[idx - 1]] : null;
            const prevSortOrder = prevStatus
              ? DocumentStatusParams[prevStatus]?.sortOrder
              : null;
            const label = aiOcrEnabled
              ? DocumentStatusParams[status]?.label
              : DocumentStatusParams[DocumentStatus.Verified].label;
            // On-flag the per-status section headers ("We'll share these…",
            // "These documents are missing…") are dropped — the single
            // "Review your uploaded documents." subtitle covers the modal and
            // each row self-describes via its variant. Off-flag keeps them.
            const showHeader =
              !aiOcrEnabled && sortOrder !== prevSortOrder && label;

            return (
              <React.Fragment key={files[i].uploadId}>
                {showHeader && (
                  <div
                    className={`file-name ${
                      label !== 'partiallyVerified' ? 'section-header' : ''
                    }`}
                  >
                    {i18n.getStr(
                      `components.Verification.AiDocReview.${label}`,
                    )}
                  </div>
                )}
                <OcrDocumentSection
                  files={files}
                  screeningId={screeningId}
                  onRemoveByUploadId={handleRemoveDocument}
                  aiOcrEnabled={aiOcrEnabled}
                  dropdown={renderDropdowns(files[i], i)}
                  handleDocumentTypeChange={handleDocumentTypeChange}
                  handleYearChange={handleYearChange}
                  documentTypes={documentTypes}
                  state={state}
                  company={company}
                  fullName={fullName}
                  acceptedTypesText={acceptedTypesText}
                  i={i}
                  setState={setState}
                  candidateDocContextEnabled={candidateDocContextEnabled}
                />
              </React.Fragment>
            );
          })}
          {state.hasMissingDocuments && (
            <p className='font-weight-light'>
              <T
                value='components.employment.missingModalDocuments'
                className='text-danger'
              />
            </p>
          )}
        </div>
        <ModalFooter
          onCancel={onCancel}
          handleSubmit={handleSubmit}
          button={aiOcrEnabled ? 'buttons.submit' : 'buttons.confirm'}
          submitDisabled={
            (aiOcrEnabled && !isOcrComplete) ||
            hasOpenEdits ||
            hasUnresolvedFailure
          }
          isGrayed={
            (!isOcrComplete && aiOcrEnabled) ||
            hasOpenEdits ||
            hasUnresolvedFailure ||
            !allFieldsFilled
          }
        />
      </div>
    </div>
  );
};
export default DocumentInfoModal;
